/**
 * Use customer route data.
 *
 * Explains routing behavior, guards, and response semantics.
 */
import { useCustomerAccountRoute } from './useCustomerAccountRoute'
import { useCustomerOrders } from './useCustomerOrders'
import { useCustomerPrivacy } from './useCustomerPrivacy'
import { useCustomerSupport } from './useCustomerSupport'

type Params = {
  currentSlug: string
  authenticated: boolean
  token: string | null
}

/**
 * Use customer route data.
 *
 * Explains routing behavior, guards, and response semantics.
 */
export const useCustomerRouteData = ({ currentSlug, authenticated, token }: Params) => {
  const {
    nextPath,
    nextPathReady,
    nestedPath,
    isDashboardRoute,
    isOrdersListRoute,
    isOrderDetailRoute,
    isSupportRoute,
    isSupportDetailRoute,
    isPrivacyRoute,
    isProfileRoute,
    isAddressesRoute,
    isPreferencesRoute,
    orderDetailId,
    supportDetailId,
    requestedAccountPath,
    checkoutResult,
    checkoutSessionId,
  } = useCustomerAccountRoute(currentSlug)

  const {
    orders,
    ordersLoading,
    ordersError,
    retryOrders,
    orderDetail,
    orderDetailLoading,
    orderDetailError,
    orderInvoices,
    orderInvoicesLoading,
    orderInvoicesError,
    checkoutSyncActive,
  } = useCustomerOrders({
    authenticated,
    token,
    loadOrdersList: isOrdersListRoute || isDashboardRoute,
    isOrderDetailRoute,
    orderDetailId,
    enableCheckoutConsistencyPolling: isOrdersListRoute && checkoutResult === 'success',
  })

  const {
    supportRequests,
    supportLoading,
    supportError,
    supportSubmitting,
    supportSuccess,
    supportSubject,
    supportMessage,
    supportOrderId,
    supportCategory,
    supportPriority,
    setSupportSubject,
    setSupportMessage,
    setSupportOrderId,
    setSupportCategory,
    setSupportPriority,
    submitSupportRequest,
    retrySupportRequests,
  } = useCustomerSupport({
    authenticated,
    loadSupportHistory: isSupportRoute || isSupportDetailRoute || isDashboardRoute,
  })

  const {
    privacyDashboard,
    privacyLoading,
    privacyError,
    privacyRefreshing,
    exportRequesting,
    exportDownloading,
    consentsSaving,
    deletionSubmitting,
    privacyNotice,
    refreshPrivacyDashboard,
    requestPrivacyExport,
    downloadPrivacyExport,
    updatePrivacyConsents,
    requestAccountDeletion,
  } = useCustomerPrivacy({
    authenticated,
    shouldLoadPrivacyDashboard: isPrivacyRoute || isDashboardRoute || isPreferencesRoute,
  })

  return {
    nextPath,
    nextPathReady,
    nestedPath,
    isDashboardRoute,
    isOrdersListRoute,
    isOrderDetailRoute,
    isSupportRoute,
    isSupportDetailRoute,
    isPrivacyRoute,
    isProfileRoute,
    isAddressesRoute,
    isPreferencesRoute,
    orderDetailId,
    supportDetailId,
    requestedAccountPath,
    checkoutResult,
    checkoutSessionId,
    orders,
    ordersLoading,
    ordersError,
    retryOrders,
    orderDetail,
    orderDetailLoading,
    orderDetailError,
    orderInvoices,
    orderInvoicesLoading,
    orderInvoicesError,
    checkoutSyncActive,
    supportRequests,
    supportLoading,
    supportError,
    supportSubmitting,
    supportSuccess,
    supportSubject,
    supportMessage,
    supportOrderId,
    supportCategory,
    supportPriority,
    setSupportSubject,
    setSupportMessage,
    setSupportOrderId,
    setSupportCategory,
    setSupportPriority,
    submitSupportRequest,
    retrySupportRequests,
    privacyDashboard,
    privacyLoading,
    privacyError,
    privacyRefreshing,
    exportRequesting,
    exportDownloading,
    consentsSaving,
    deletionSubmitting,
    privacyNotice,
    refreshPrivacyDashboard,
    requestPrivacyExport,
    downloadPrivacyExport,
    updatePrivacyConsents,
    requestAccountDeletion,
  }
}
